<template>
    <div>
        <div class="flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
            <div class="hidden md:block"></div>
            <h1 class="text-3xl text-center">
                Clientes en total
                <strong class="text-theme-primary">
                    {{ itemsNumber }}
                </strong>
            </h1>
            <div class="flex justify-center md:justify-end">
                <app-button
                    v-if="$ability.hasPermission('customer:Create')"
                    color="success"
                    size="large"
                    @click="$router.push('/app/customers/new')"
                >
                    <span class="hidden md:inline">Nuevo cliente</span>
                    <span class="md:hidden font-bold">+</span>
                </app-button>
            </div>
        </div>

        <div class="flex flex-wrap mt-2">
            <div class="w-full">
                <customers-filters v-model="query" @enter="reloadItems" />
                <div class="flex justify-center mt-2">
                    <app-button v-show="query" relief @click="reloadItems">
                        Consultar 🔎
                    </app-button>
                </div>
            </div>
            <div class="flex justify-between flex-col lg:flex-row w-full mt-3">
                <div class="flex items-center justify-around"></div>

                <div class="flex items-center justify-around">
                    <list-settings-button
                        :value="{ pageSize }"
                        @change="onListSettingsChange"
                    />
                    <custom-pagination
                        v-model="currentPage"
                        :length="pagesNumber"
                        @input="listItems()"
                    />
                </div>
            </div>
        </div>

        <div class="flex flex-wrap mt-4">
            <custom-table
                ref="selectableTable"
                :fields="$data.$fields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                select-mode="multi"
                responsive
                outlined
                no-border-collapse
                hover
                selectable
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
                @flex-selected="onRowSelected"
            >
                <template #cell(id)="data">
                    <div class="flex items-center gap-2">
                        <router-link
                            :to="`/app/customers/detail/${data.item.id}`"
                            class="text-theme-primary"
                            title="Ver detalle"
                        >
                            <chevron-right-icon class="w-4 h-4" />
                        </router-link>
                        <uuid-display :value="data.item.id" />
                    </div>
                </template>
                <template #cell(identificationNumber)="data">
                    <span
                        v-if="customerIdentification(data.item)"
                        class="text-center"
                    >
                        {{ customerIdentification(data.item) }}
                    </span>
                    <span v-else class="text-center text-theme-muted">
                        {{ emptyIdentification }}
                    </span>
                </template>
                <template #cell(firstName)="data">
                    <span class="text-center capitalize">
                        {{ data.item.firstName }} {{ data.item.lastName }}
                    </span>
                </template>
                <template #cell(phone)="data">
                    <span class="text-center">
                        {{ data.item.phone }}
                    </span>
                </template>
                <template #cell(email)="data">
                    <span v-if="data.item.email" class="text-center">
                        {{ data.item.email }}
                    </span>
                    <span v-else class="text-center text-theme-muted">
                        {{ emptyEmail }}
                    </span>
                </template>
                <template #cell(createdAt)="data">
                    <span class="text-center">
                        {{ data.item.createdAt | date }}
                    </span>
                </template>
                <template #cell(updatedAt)="data">
                    <span class="text-center">
                        {{ data.item.updatedAt | date }}
                    </span>
                </template>
            </custom-table>
        </div>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import {
    CUSTOMER_EMPTY_EMAIL,
    CUSTOMER_EMPTY_IDENTIFICATION,
    formatCustomerIdentification
} from "@/common/lib/order-customer";

import CustomersFilters from "@/components/customers/CustomersFilters.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import CustomTable from "@/components/utils/CustomTable.vue";
import UuidDisplay from "@/components/utils/UuidDisplay.vue";

export default {
    name: "IndexCustomers",
    components: {
        CustomersFilters,
        CustomTable,
        ChevronRightIcon,
        UuidDisplay
    },
    mixins: [urlQuerySyncMixin],
    data: () => ({
        itemsCurrent: [],
        selected: [],
        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        pageSize: 24,
        $fields: [
            { key: "id", label: "ID", sortable: false },
            {
                key: "identificationNumber",
                label: "Identificación",
                sortable: false
            },

            {
                key: "firstName",
                label: "Nombres",
                sortable: false
            },
            {
                key: "phone",
                label: "Célular",
                sortable: false
            },
            {
                key: "email",
                label: "Correo",
                sortable: false
            },
            { key: "updatedAt", label: "🗓 Actualización", sortable: true },
            { key: "createdAt", label: "🗓 Creación", sortable: true }
        ],
        query: {},
        $urlPaginationKey: "page",
        $urlFilterSchema: [
            { key: "email", type: "string" },
            { key: "phone", type: "string" },
            { key: "identificationNumber", type: "string" },
            { key: "fullName", type: "string" }
        ]
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        emptyEmail() {
            return CUSTOMER_EMPTY_EMAIL;
        },
        emptyIdentification() {
            return CUSTOMER_EMPTY_IDENTIFICATION;
        }
    },
    watch: {},
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("customer:List", "/app"))) return;
        this.listItems(true);
    },
    methods: {
        ...mapActions("customers", ["listCustomersPaginated"]),
        customerIdentification(customer) {
            return formatCustomerIdentification(customer);
        },
        async listItems(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items, pagination } = await this.listCustomersPaginated(
                    {
                        ...this.query,
                        complete,
                        itemsByPage: this.pageSize,
                        page: this.currentPage
                    }
                );
                this.itemsCurrent = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize }) {
            this.pageSize = pageSize;
            this.reloadItems();
        },
        onRowSelected(items) {
            this.selected = items;
        },
        clearSelected() {
            this.$refs.selectableTable.clearSelected();
            this.selected = [];
        }
    }
};
</script>
